import { PositionType } from 'data/types';
import {
  boundingBoxOfVertices,
  isRectangularVertices,
  normalizeVerticesToOrigin
} from 'utils/space-polygon';
import { FootprintRectangleDimensions } from './footprint-rectangle-dimensions-types';

const CORNER_TOLERANCE_FT = 0.5;

function ringNeighbor(
  points: PositionType[],
  index: number,
  step: number
): PositionType {
  return points[(index + step + points.length) % points.length];
}

function isSamePoint(a: PositionType, b: PositionType): boolean {
  return a.x === b.x && a.y === b.y;
}

function distanceToChord(
  point: PositionType,
  from: PositionType,
  to: PositionType
): number {
  const chordLength = Math.hypot(to.x - from.x, to.y - from.y);
  if (chordLength === 0) return Infinity;
  const cross =
    (to.x - from.x) * (point.y - from.y) - (to.y - from.y) * (point.x - from.x);
  return Math.abs(cross) / chordLength;
}

function isCorner(points: PositionType[], index: number): boolean {
  return (
    distanceToChord(
      points[index],
      ringNeighbor(points, index, -1),
      ringNeighbor(points, index, 1)
    ) >= CORNER_TOLERANCE_FT
  );
}

function footprintCorners(points: PositionType[]): PositionType[] {
  const distinct = points.filter(
    (point, index) => !isSamePoint(point, ringNeighbor(points, index, 1))
  );
  return distinct.filter((_, index) => isCorner(distinct, index));
}

export function getRectangularFootprintDimensions(
  points: PositionType[]
): FootprintRectangleDimensions | null {
  const corners = footprintCorners(points);
  const box = boundingBoxOfVertices(corners);
  const { vertices } = normalizeVerticesToOrigin(corners);
  if (
    !isRectangularVertices(vertices, box.width, box.height, CORNER_TOLERANCE_FT)
  ) {
    return null;
  }
  return { width: box.width, length: box.height };
}

export function formatFootprintDimensions(
  dimensions: FootprintRectangleDimensions
): string {
  return `${Math.round(dimensions.width)}ft × ${Math.round(
    dimensions.length
  )}ft`;
}
